import { enablePatches, Patch } from 'immer';

// Required for the produceWithPatches/applyPatches machinery used with these stacks.
enablePatches();

/** Maximum retained undo entries; the oldest entry is dropped beyond this. */
export const MAX_UNDO_DEPTH = 100;

/**
 * Same-key entries recorded further apart than this never coalesce. Bounds a
 * coalescing run to one editing session: without it, renaming a node, coming back
 * minutes later, and renaming it again would merge into a single undo step and make
 * the intermediate name unrecoverable.
 */
export const COALESCE_WINDOW_MS = 5000;

/** One undoable mutation: immer patches to replay it (`redo`) or reverse it (`undo`). */
export interface UndoRedoEntry {
    undo: Patch[];
    redo: Patch[];
    coalesceKey?: string;
}

/**
 * Session-scoped undo/redo history over immer patch sets (in-memory only; never
 * persisted). Owns the stacks, the depth cap, and the coalescing rules; the document
 * owner ({@link TreeInteractor}) records each undoable mutation here and applies the
 * patch sets that undo()/redo() return.
 */
export class UndoRedoInteractor {
    private undoStack: UndoRedoEntry[] = [];
    private redoStack: UndoRedoEntry[] = [];
    /** Key of the coalescing run the top undo entry belongs to, if one is in progress. */
    private activeCoalesceKey: string | null = null;
    /** When the last entry was recorded; entries outside COALESCE_WINDOW_MS never merge. */
    private lastRecordAtMs = Number.NEGATIVE_INFINITY;

    constructor(private readonly now: () => number = Date.now) {}

    get canUndo(): boolean {
        return this.undoStack.length > 0;
    }

    get canRedo(): boolean {
        return this.redoStack.length > 0;
    }

    /**
     * Records an undoable mutation and clears the redo stack. Consecutive entries with
     * the same coalesceKey recorded within {@link COALESCE_WINDOW_MS} of the previous
     * entry merge into one undo step (the run's original undo patches, the latest redo
     * patches) — e.g. a debounced typing burst. Any entry with a different/absent key,
     * a gap longer than the window, an undo/redo, or {@link endCoalescing} ends the run.
     */
    record(entry: UndoRedoEntry): void {
        const at = this.now();
        const withinWindow = at - this.lastRecordAtMs <= COALESCE_WINDOW_MS;
        const top = this.undoStack[this.undoStack.length - 1];
        if (withinWindow && entry.coalesceKey && entry.coalesceKey === this.activeCoalesceKey && top?.coalesceKey === entry.coalesceKey) {
            top.redo = entry.redo;
        } else {
            this.undoStack.push(entry);
            if (this.undoStack.length > MAX_UNDO_DEPTH) {
                this.undoStack.shift();
            }
        }
        this.redoStack = [];
        this.activeCoalesceKey = entry.coalesceKey ?? null;
        this.lastRecordAtMs = at;
    }

    /** Ends any in-progress coalescing run (call for mutations that are not recorded). */
    endCoalescing(): void {
        this.activeCoalesceKey = null;
    }

    /**
     * Moves the most recent entry to the redo stack and returns the patches that
     * reverse it, or null when there is nothing to undo.
     */
    undo(): Patch[] | null {
        const entry = this.undoStack.pop();
        if (!entry) return null;
        this.redoStack.push(entry);
        this.activeCoalesceKey = null;
        return entry.undo;
    }

    /**
     * Moves the most recently undone entry back to the undo stack and returns the
     * patches that replay it, or null when there is nothing to redo.
     */
    redo(): Patch[] | null {
        const entry = this.redoStack.pop();
        if (!entry) return null;
        this.undoStack.push(entry);
        this.activeCoalesceKey = null;
        return entry.redo;
    }

    /**
     * Drops both stacks. Patches are only valid against the document lineage they were
     * recorded on, so callers must reset whenever that lineage is replaced.
     */
    reset(): void {
        this.undoStack = [];
        this.redoStack = [];
        this.activeCoalesceKey = null;
    }
}
